'use client';

import Button from 'components/shared/Buttons/BaseButton';
import { useBreakpoint } from 'hooks/useBreakpoints';
import usePrevious from 'hooks/usePrevious';
import { AnimationSequence, useAnimate } from 'motion/react';
import { useEffect } from 'react';
import { BUTTON_DURATION, BUTTON_DURATION_PART_1, BUTTON_DURATION_PART_2 } from './animations';
import { HEADER_NAV_ID_PREFIX } from 'components/MainNav/utils';

interface NavMenuButtonProps {
  menuIsOpen: boolean;
  subMenuIsOpen: boolean;
  nestedSubMenuIsOpen?: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  closeSubMenu: () => void;
  closeNestedSubMenu?: () => void;
  idSuffix: string;
}

const hamburgerToXAnimation: AnimationSequence = [
  // Transition menu from close to open
  [
    '#line-1',
    { rotate: -45, x: 5, y: 5, width: 24, originX: '50%', originY: '50%' },
    { duration: BUTTON_DURATION, at: '<' },
  ],
  [
    '#line-2',
    { rotate: 45, x: 0, y: 0, width: 24, originX: '50%', originY: '50%' },
    { duration: BUTTON_DURATION, at: '<' },
  ],
  ['#line-3', { opacity: 0 }, { duration: BUTTON_DURATION, at: '<' }],
];

const backToHamburgerAnimation: AnimationSequence = [
  // Transition back to menu closed from either submenu open or menu open
  [
    '#line-1',
    { rotate: 0, x: 0, y: 0, width: 24, originX: '50%', originY: '50%' },
    { duration: BUTTON_DURATION, at: '<' },
  ],
  [
    '#line-2',
    { rotate: 0, x: 0, y: 0, width: 24, originX: '50%', originY: '50%' },
    { duration: BUTTON_DURATION, at: '<' },
  ],
  ['#line-3', { opacity: 1 }, { duration: BUTTON_DURATION, at: '<' }],
];

const xToArrowLeftAnimation: AnimationSequence = [
  // Transition from menu to submenu
  // Total duration is the same as BUTTON_DURATION
  ['#line-1', { rotate: [-45, 0], width: [24, 2], x: [5, 12], y: [5, 6] }, { duration: BUTTON_DURATION_PART_1 }],
  [
    '#line-2',
    { rotate: [45, 0], width: [24, 2], x: [0, 12], y: [0, 2] },
    { duration: BUTTON_DURATION_PART_1, at: '<' },
  ],

  ['#line-1', { rotate: [0, -45], x: [12, 7], y: [6, -3], width: [2, 13] }, { duration: BUTTON_DURATION_PART_2 }],
  [
    '#line-2',
    { rotate: [0, 45], x: [12, 2], y: [2, 8], width: [2, 13] },
    { duration: BUTTON_DURATION_PART_2, at: '<' },
  ],
];

const arrowLeftToXAnimation: AnimationSequence = [
  // Transition from submenu to menu
  // Total duration is the same as BUTTON_DURATION
  ['#line-1', { rotate: [-45, 0], x: [7, 12], y: [-3, 6], width: [13, 2] }, { duration: BUTTON_DURATION_PART_2 }],
  [
    '#line-2',
    { rotate: [45, 0], x: [2, 12], y: [8, 2], width: [13, 2] },
    { duration: BUTTON_DURATION_PART_2, at: '<' },
  ],

  ['#line-1', { rotate: [0, -45], width: [2, 24], x: [12, 5], y: [6, 5] }, { duration: BUTTON_DURATION_PART_1 }],
  [
    '#line-2',
    { rotate: [0, 45], width: [2, 24], x: [12, 0], y: [2, 0] },
    { duration: BUTTON_DURATION_PART_1, at: '<' },
  ],
];

function NavMenuButton({
  menuIsOpen,
  subMenuIsOpen,
  nestedSubMenuIsOpen = false,
  openMenu,
  closeMenu,
  closeSubMenu,
  closeNestedSubMenu,
  idSuffix,
}: NavMenuButtonProps) {
  const [scope, animate] = useAnimate();
  const prevMenuIsOpen = usePrevious(menuIsOpen);
  const prevSubMenuIsOpen = usePrevious(subMenuIsOpen);
  const { isAboveSm } = useBreakpoint('sm');

  const getMenuActionAndLabel = () => {
    const shouldCloseNestedSubMenu = nestedSubMenuIsOpen && isAboveSm && closeNestedSubMenu;
    const shouldCloseSubMenu = subMenuIsOpen && isAboveSm;
    const shouldCloseMenu = (menuIsOpen && !subMenuIsOpen) || (subMenuIsOpen && !isAboveSm);

    if (shouldCloseNestedSubMenu) {
      return { action: closeNestedSubMenu, label: 'Back', id: 'back_button' };
    }

    if (shouldCloseSubMenu) {
      return { action: closeSubMenu, label: 'Back', id: 'back_button' };
    }

    if (shouldCloseMenu) {
      return { action: closeMenu, label: 'Close Menu', id: 'menu_close' };
    }

    // Button should open menu by default
    return { action: openMenu, label: 'Open Menu', id: 'menu_open' };
  };

  useEffect(() => {
    const menuWasOpened = !prevMenuIsOpen && menuIsOpen;
    const menuWasClosed = !menuIsOpen && prevMenuIsOpen;
    // Submenu transitions are not used on mobile
    const subMenuWasOpened = subMenuIsOpen && !prevSubMenuIsOpen;
    const subMenuOnlyWasClosed = menuIsOpen && !subMenuIsOpen && prevSubMenuIsOpen;

    if (menuWasOpened) {
      animate(hamburgerToXAnimation);
    }

    if (subMenuWasOpened && isAboveSm) {
      animate(xToArrowLeftAnimation);
    }

    if (subMenuOnlyWasClosed && isAboveSm) {
      animate(arrowLeftToXAnimation);
    }

    if (menuWasClosed) {
      animate(backToHamburgerAnimation);
    }
  }, [subMenuIsOpen, menuIsOpen, isAboveSm, prevMenuIsOpen, prevSubMenuIsOpen, animate]);

  const { action, label, id } = getMenuActionAndLabel();

  return (
    <Button
      variant="icon"
      label={label}
      onClick={action}
      className="pl-0"
      id={`${HEADER_NAV_ID_PREFIX}_${id}_${idSuffix}`}>
      <svg
        ref={scope}
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="pointer-events-none **:pointer-events-none">
        {/* The line offsets/origins below are tuned around the SVG viewport centre (12,12). motion ≥12.40
            forces `transform-box: fill-box` on any transformed SVG node, which re-anchors `originX/Y: 50%`
            to each bar's own box and skews the X/arrow. Pin view-box (`!` beats motion's inline style) so the
            origin stays the viewport centre these keyframes assume. */}
        <rect id="line-1" y="4" width="24" height="2" fill="currentColor" className="[transform-box:view-box]!" />
        <rect id="line-2" y="11" width="24" height="2" fill="currentColor" className="[transform-box:view-box]!" />
        <rect id="line-3" y="18" width="24" height="2" fill="currentColor" className="[transform-box:view-box]!" />
      </svg>
    </Button>
  );
}

export default NavMenuButton;
